<template>
    <div v-if="visible" class="filter-suggest-dropdown position-absolute" :class="modifierClass">
        <div v-if="loading" class="filter-suggest-dropdown-state">
            <span class="filter-suggest-dropdown-spinner" aria-hidden="true"></span>
            <span>{{ loadingMessage }}</span>
        </div>
        <div v-else-if="items.length === 0" class="filter-suggest-dropdown-state">
            <span>{{ emptyMessage }}</span>
        </div>
        <template v-else>
            <div class="filter-suggest-dropdown-header">
                <div class="filter-suggest-dropdown-header-start">
                    <slot name="header-actions" />
                    <span v-if="!hasHeaderActions" class="filter-suggest-dropdown-meta">
                        {{ items.length }} results
                    </span>
                </div>
                <button type="button" class="filter-suggest-dropdown-close" :aria-label="closeAriaLabel"
                    @click="$emit('close')">
                    <MDBIcon iconStyle="fas" icon="times" />
                </button>
            </div>
            <div v-if="hasHeaderActions" class="filter-suggest-dropdown-meta-row">
                <span>{{ items.length }} results</span>
                <span v-if="selectedCount > 0" class="filter-suggest-dropdown-selected">
                    {{ selectedCount }} selected
                </span>
            </div>
            <div class="filter-suggest-dropdown-list" role="listbox" :aria-label="listAriaLabel">
                <template v-for="(item, index) in items" :key="itemKey(item, index)">
                    <slot name="option" :item="item" :index="index">
                        <button type="button" class="filter-suggest-dropdown-option"
                            @click="$emit('select', item)">
                            {{ formatItemLabel(item) }}
                        </button>
                    </slot>
                </template>
            </div>
        </template>
    </div>
</template>

<script setup>
import { MDBIcon } from 'mdb-vue-ui-kit';
import { computed, defineEmits, defineProps, useSlots } from 'vue';

const props = defineProps({
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    loadingMessage: { type: String, default: 'Searching…' },
    emptyMessage: { type: String, default: 'No results found.' },
    listAriaLabel: { type: String, default: 'Suggestions' },
    closeAriaLabel: { type: String, default: 'Close list' },
    modifierClass: { type: String, default: '' },
    selectedCount: { type: Number, default: 0 },
    itemLabel: { type: Function, default: null },
});

defineEmits(['close', 'select']);

const slots = useSlots();
const hasHeaderActions = computed(() => Boolean(slots['header-actions']));

const itemKey = (item, index) => item?.id ?? index;

const formatItemLabel = (item) => {
    if (props.itemLabel) {
        return props.itemLabel(item);
    }
    if (item?.name) {
        return item.name;
    }
    if (item?.firstName != null) {
        return `${item.firstName} ${item.lastName}`.trim();
    }
    return String(item ?? '');
};
</script>

<style scoped>
.filter-suggest-dropdown {
    z-index: 9999;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    min-width: 190px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

.filter-suggest-dropdown--claimant {
    z-index: 9998;
}

.filter-suggest-dropdown-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem 0.75rem;
    font-size: 13px;
    color: #6c757d;
}

.filter-suggest-dropdown-spinner {
    width: 1rem;
    height: 1rem;
    border: 2px solid #dee2e6;
    border-top-color: var(--primary, #0d6efd);
    border-radius: 50%;
    animation: filter-suggest-spin 0.7s linear infinite;
}

@keyframes filter-suggest-spin {
    to {
        transform: rotate(360deg);
    }
}

.filter-suggest-dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    border-bottom: 1px solid #eee;
    background: #f8f9fa;
}

.filter-suggest-dropdown-header-start {
    flex: 1;
    min-width: 0;
}

.filter-suggest-dropdown-meta {
    font-size: 12px;
    color: #6c757d;
    white-space: nowrap;
}

.filter-suggest-dropdown-meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    font-size: 12px;
    color: #6c757d;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
}

.filter-suggest-dropdown-selected {
    font-weight: 600;
    color: var(--primary, #0d6efd);
}

.filter-suggest-dropdown-close {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #6c757d;
    cursor: pointer;
}

.filter-suggest-dropdown-close:hover {
    background: #e9ecef;
    color: #212529;
}

.filter-suggest-dropdown-list {
    max-height: 280px;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.filter-suggest-dropdown-option {
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: none;
    border-bottom: 1px solid #f0f0f0;
    background: transparent;
    font-size: 13px;
    color: #212529;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.filter-suggest-dropdown-option:last-child {
    border-bottom: none;
}

.filter-suggest-dropdown-option:hover {
    background: #f8f9fa;
}

.filter-suggest-dropdown-option:focus-visible {
    outline: 2px solid var(--primary, #0d6efd);
    outline-offset: -2px;
}

.filter-suggest-dropdown-header-start :deep(.filter-suggest-dropdown-link) {
    border: none;
    background: none;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary, #0d6efd);
    cursor: pointer;
    white-space: nowrap;
}

.filter-suggest-dropdown-header-start :deep(.filter-suggest-dropdown-link:hover) {
    text-decoration: underline;
}

.filter-suggest-dropdown-header-start :deep(.filter-suggest-dropdown-link-muted) {
    color: #6c757d;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-item) {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-item:last-child) {
    border-bottom: none;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-item:hover) {
    background: #f8f9fa;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-item.is-selected) {
    background: #e8f0fe;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-name) {
    font-size: 13px;
    color: #212529;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-favorite) {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-favorite:hover) {
    background: #fff3cd;
}

.filter-suggest-dropdown-list :deep(.filter-suggest-dropdown-favorite .text-muted) {
    opacity: 0.45;
}
</style>
